import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const PrintifySidebarManager = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [sidebarConfig, setSidebarConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const { user } = useAuth();

  const API = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchProducts();
    fetchSidebarConfig();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/api/printify/products`);
      if (response.data.success) {
        setAllProducts(response.data.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchSidebarConfig = async () => {
    try {
      const response = await axios.get(`${API}/api/printify/sidebar-products`);
      if (response.data.success) {
        const configuredIds = response.data.products.map(p => p.id);
        setSidebarConfig(configuredIds);
      }
    } catch (error) {
      console.error('Error fetching sidebar config:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (productId) => {
    setSidebarConfig(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        // Limit to 3 products
        if (prev.length >= 2) {
          setMessage('⚠️ Maximum 3 products allowed in sidebar. Remove one first.');
          setTimeout(() => setMessage(''), 3000);
          return prev;
        }
        return [...prev, productId];
      }
    });
  };

  const saveConfiguration = async () => {
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/printify/sidebar-config`,
        { product_ids: sidebarConfig },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setMessage('✅ Sidebar configuration saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('❌ Failed to save configuration');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      setMessage('❌ Error saving configuration');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (variants) => {
    if (!variants || variants.length === 0) return 'N/A';
    
    const prices = variants.map(v => parseFloat(v.price) / 100);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    if (minPrice === maxPrice) {
      return `$${minPrice.toFixed(2)}`;
    }
    return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Sidebar Products Manager</h2>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Sidebar Products Manager</h2>
        <div className="text-sm text-gray-600">
          {sidebarConfig.length}/2 products selected
        </div>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.includes('✅') ? 'bg-green-50 text-green-800' : 
          message.includes('⚠️') ? 'bg-yellow-50 text-yellow-800' : 
          'bg-red-50 text-red-800'
        }`}>
          {message}
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          Select up to 2 products to display in the top sidebar:
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          These products will appear in the "Conscious Commerce" section of the main dashboard sidebar.
        </p>
      </div>

      {allProducts.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 mb-4">No products found in your Printify store</div>
          <button
            onClick={fetchProducts}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Refresh Products
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {allProducts.map((product) => {
              const isSelected = sidebarConfig.includes(product.id);
              
              return (
                <div
                  key={product.id}
                  className={`relative rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                    isSelected 
                      ? 'border-green-500 bg-green-50 shadow-lg transform scale-105' 
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
                  }`}
                  onClick={() => toggleProduct(product.id)}
                >
                  {/* Selection indicator */}
                  <div className="absolute -top-2 -right-2 z-10">
                    {isSelected ? (
                      <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                        ✓
                      </div>
                    ) : (
                      <div className="bg-gray-300 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold hover:bg-blue-500 hover:text-white transition-colors">
                        +
                      </div>
                    )}
                  </div>

                  {/* Product Image */}
                  {product.images && product.images.length > 0 && (
                    <div className="aspect-square overflow-hidden rounded-t-xl">
                      <img
                        src={product.images[0]}
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Product Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2">
                      {product.title}
                    </h3>
                    
                    <div className="text-lg font-bold text-green-600 mb-2">
                      {formatPrice(product.variants)}
                    </div>

                    {/* Tags */}
                    {product.tags && product.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {product.tags.slice(0, 2).map((tag, index) => (
                          <span 
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                        {product.tags.length > 2 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                            +{product.tags.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Selected overlay */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-green-500 bg-opacity-10 rounded-xl pointer-events-none"></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Save button */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              {sidebarConfig.length > 0 ? (
                <span>
                  {sidebarConfig.length} product{sidebarConfig.length !== 1 ? 's' : ''} selected for sidebar display
                </span>
              ) : (
                <span>No products selected - sidebar will show first 2 products by default</span>
              )}
            </div>
            
            <button
              onClick={saveConfiguration}
              disabled={saving}
              className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 disabled:opacity-50 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-105"
            >
              {saving ? 'Saving...' : 'Save Sidebar Configuration'}
            </button>
          </div>
        </>
      )}

      {/* Preview section */}
      {sidebarConfig.length > 0 && (
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Sidebar Preview:</h3>
          <div className="bg-gray-50 rounded-xl p-6">
            <h4 className="font-bold text-gray-800 mb-2">Conscious Commerce</h4>
            <p className="text-xs text-gray-600 mb-4 leading-relaxed">
              What if shopping could change the world? With our Triple Win™ model, every purchase uplifts you, 
              strengthens the community, and gives back to the planet—33% of proceeds go straight to a designated charity.
            </p>
            <div className="space-y-3">
              {allProducts
                .filter(p => sidebarConfig.includes(p.id))
                .slice(0, 3)
                .map((product) => (
                  <div key={product.id} className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center space-x-3">
                      {product.images && product.images.length > 0 && (
                        <img
                          src={product.images[0]}
                          alt={product.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-medium text-gray-800 truncate">
                          {product.title}
                        </h5>
                        <p className="text-sm font-bold text-green-600">
                          {formatPrice(product.variants)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintifySidebarManager;